
import { createSelector } from 'reselect';
import { Map as ImmutableMap } from 'immutable';
import moment from 'moment';
import { isAcme } from 'isAcme';
import { createTrimSelector } from 'utils/selectorsHelpers';
import { getLogger } from 'utils/logger';

import {
  getAccountsById,
  getInvestmentAccounts,
  getGoalAccountIds,
  getPureManualAccounts,
  getIsLoading as accountsLoading,
  getShowGoalAccounts,
  getAccountsByIdIncludingGoals,
} from 'data/accounts/selectors';
import { getTransactionsStore, getTransactionsByAccountId, getIsLoading as transactionsLoading, TxnTypes} from 'data/transactions/selectors';
import { getIHoldsByAccountId } from 'data/investmentHoldings/selectors';
import { isUnacceptedScheduledTxn, nextScheduledInstanceAfterDate } from 'data/transactions/utils';
import { getReminderSettingInDays } from 'data/accounts/utils';
import { getOptimalCache, setOptimalCache } from 'data/optimalCacheManager';
import { getInvestmentHoldingsBalancesByAccountId } from 'data/investmentHoldingsV2/investmentHoldingsV2Selectors';
import { noNaN } from 'utils/utils';

import { AccountBalances } from './types';

import { getBalMap } from './balCache';

const log = getLogger('data/accountBalances/selectors');

export function isConnectedAccount(account) {
  return account.institutionLoginId &&
    account.aggregators && account.aggregators.size > 0;
}

// TODO: Should have dataset preference to include-goal-txns-in-balances  (nice job commenting Ken!, first one EVER)
export const getIncludeGoalTxnsInBalances = createSelector(
  getShowGoalAccounts,
  (showGoalAccounts) => showGoalAccounts,
);

export function includeRecurringTxn(txn, recurringSetting, transactionsForAccountId) {

  // "next scheduled instance", but this needs to be the next scheduled instance after TODAY
  // so it still shows all prior instances

  // For Acme, we do not include ANY unaccepted (SCHEDULED_PENDING) transactions in the balance
  if (isAcme) {
    return false;
  }

  if (noNaN(recurringSetting) === -1) {
    // include all due and overdue
    if (moment(txn.postedOn).isSameOrBefore(moment(), 'day')) {
      return true;
    }
    const nextInstanceAfterToday = nextScheduledInstanceAfterDate(moment(), transactionsForAccountId, txn.stModelId);
    return nextInstanceAfterToday && (txn.id === nextInstanceAfterToday.id);
  }
  if (noNaN(recurringSetting) === 0) {
    return false;
  }
  const mostRecentSchedTxnDate = moment().add(noNaN(recurringSetting), 'days');
  return moment(txn.postedOn).isSameOrBefore(mostRecentSchedTxnDate, 'day');
}

function includeTransactionInBalance(
  txn,
  {
    goalAccountIds,
    doIncludeGoalTxnsInBalances,
    recurringSetting,
    transactionsForAccountId,
  }
) {
  // If this is a goal txn and it is not in a goal account and includeGoalTxnsInBalances if false,
  // then do not include in balance.

  if (!doIncludeGoalTxnsInBalances
    && txn.coa && txn.coa.type === 'ACCOUNT' && goalAccountIds.contains(txn.coa.id)
    && !goalAccountIds.contains(txn.accountId))
  {
    return false;
  }

  if (isUnacceptedScheduledTxn(txn)) {
    return includeRecurringTxn(txn, recurringSetting, transactionsForAccountId);
  }

  return true;
}

const balRound = (val) => val && Number(val.toFixed(2));

const optimalCacheCompareFn = (oldTxn, newTxn, accountId) => {

  // if this txn change does not involve this account, then no recalc needed
  if (oldTxn.accountId !== accountId && newTxn.accountId !== accountId) {
    return true;
  }

  // if there is no oldTxn (add), then return false
  if (!oldTxn) return false;
  //
  // Need to return false for all cases in which a change in a transaction field can cause a balance to change
  //
  if (newTxn.accountId !== oldTxn.accountId) return false;
  if (newTxn.isDeleted) return false;
  // did we move forward or backwards relative to today?
  if ((moment(oldTxn.postedOn).isSameOrBefore(moment(), 'day') && moment(newTxn.postedOn).isAfter(moment(), 'day')) ||
    (moment(newTxn.postedOn).isSameOrBefore(moment(), 'day') && moment(oldTxn.postedOn).isAfter(moment(), 'day'))) {
    return false;
  }
  if (isAcme) return oldTxn.amount === newTxn.amount;
  return (oldTxn.amount === newTxn.amount && oldTxn.postedOn === newTxn.postedOn);
};

export const getBalancesByAccountId = createSelector(
  getAccountsByIdIncludingGoals,
  (state) => getTransactionsByAccountId(state, { txnTypes: TxnTypes.REGULAR_TXNS | TxnTypes.UNACCEPTED_SCHEDULED_TXNS | TxnTypes.GOALS }),
  transactionsLoading,
  accountsLoading,
  getPureManualAccounts,
  getGoalAccountIds,
  getIncludeGoalTxnsInBalances,
  getTransactionsStore,
  (accounts, transactionsByAccountId, transactionsAreLoading,
    accountsAreLoading, pureManualAccounts, goalAccountIds, includeGoalTxnsInBalances, transactionsStore) =>

    (new ImmutableMap()).withMutations((balancesByAccountId) => {

      const balMap = getBalMap();

      accounts.forEach((account) => {

        const isConnected = isConnectedAccount(account); // !pureManualAccounts.find((x) => x.id === account.id);

        if (account.type === 'INVESTMENT') return;

        const txns = transactionsByAccountId.get(account.id);

        let acctReminderSetting = 0;
        acctReminderSetting = getReminderSettingInDays(account.recurringTxn);

        const cacheKey = account.id;
        const cacheRec = balMap[cacheKey];
        const cacheBroke =
          (!cacheRec) ||
          (cacheRec.txns !== txns) ||
          (cacheRec.acctReminderSetting !== acctReminderSetting) ||
          (cacheRec.includeGoalTxnsInBalances !== includeGoalTxnsInBalances) ||
          (cacheRec.account.currentBalanceAsOf !== account.currentBalanceAsOf) ||
          (cacheRec.account.onlineBalance !== account.onlineBalance) ||
          (cacheRec.account.balanceAsOf !== account.balanceAsOf) ||
          (cacheRec.account.currentBalanceAsOfOn !== account.currentBalanceAsOfOn) ||
          (cacheRec.account.balanceAsOfOn !== account.balanceAsOfOn);

        if (!cacheBroke) {
          const endBal = balMap[cacheKey] ? balMap[cacheKey].endingBalance : 0;
          const endBalNoRecurring = balMap[cacheKey] ? balMap[cacheKey].endingBalanceNoRecurring : 0;
          const currBal = balMap[cacheKey] ? balMap[cacheKey].currentBalance : 0;
          const finalBal = balMap[cacheKey] ? balMap[cacheKey].finalBalance : 0;
          const goalsBal = balMap[cacheKey] ? balMap[cacheKey].goalsBalance : 0;
          const txnsDeltaFromCurrentBalance = balMap[cacheKey] ? balMap[cacheKey].txnsDeltaFromCurrentBalance : 0;
          const eDate = balMap[cacheKey] ? balMap[cacheKey].earliestTransactionDate : null;
          balancesByAccountId.set(account.id, new AccountBalances({
            accountId: account.id,
            accountType: account.type,
            endingBalance: endBal,
            endingBalanceNoRecurring: endBalNoRecurring,
            onlineBalance: !isConnected ? currBal : account.onlineBalance,
            currentBalance: currBal,
            finalBalance: finalBal,
            goalsBalance: goalsBal,
            earliestTransactionDate: eDate,
            txnsDeltaFromCurrentBalance,
          }));
          // log.log('======================= CACHE HIT OR EMPTY TXNS FOR ', account.name);
          return;
        }

        // TODO: Ask Tim what to do about includeGoalTxnsInBalances flag

        // We use the optimal cache as well if the local cache fails, to see if we can still bypass the calculation
        const cacheString = `acctBal:${account.id}-${acctReminderSetting}-${account.current}` +
          `-${account.onlineBalance}-${account.balanceAsOf}-${account.currentBalanceAsOfOn}-${account.balanceAsOfOn}`;
        const strictCacheString = `${cacheString}-${txns && txns.hashCode()}`;

        const cachedValues = getOptimalCache(
          transactionsStore,
          'acctBalances',
          cacheString,
          strictCacheString,
          (oldTxn, newTxn) => optimalCacheCompareFn(oldTxn, newTxn, account.id),
          (currentCache) => currentCache,
        );
        if (cachedValues) {
          // log.log('OPTIMAL CACHE USED =========================================', account.id);
          balancesByAccountId.set(account.id, cachedValues);
          return;
        }


        let endingBalance = 0;
        let endingBalanceNoRecurring = 0;
        let currentBalance = account.currentBalanceAsOf;
        let txnsDeltaFromCurrentBalance = null;
        let earliestTransactionDate = null;
        let finalBalance = 0;
        let goalsBalance = 0;

        // const mostRecentSchedTxnDate = moment().add(Number(acctReminderSetting), 'days');

        let setCache = false;
        // just use defaults until transactions are available
        if (txns && !transactionsAreLoading && !accountsAreLoading) {

          setCache = true;

          currentBalance = 0;
          // if we have not already calculated the sum of transactions downloaded which match the accounts
          // currentBalance values, and the account has a currentBalance, use it to do the calc
          //
          const calculateTxnDelta =
            account.currentBalanceAsOf && (
              (!cacheRec) ||
              (cacheRec.txnsDeltaFromCurrentBalance === null) ||
              (account.currentBalanceAsOf && account.currentBalanceAsOf !== cacheRec.account.currentBalanceAsOf) ||
              (account.currentBalanceAsOfOn && account.currentBalanceAsOfOn !== cacheRec.account.currentBalanceAsOfOn)
            );

          log.log('======================= RECALCULATING FOR ', account.name, calculateTxnDelta, account.currentBalanceAsOf);
          // log.log('currentBalance is ', account.currentBalanceAsOf);
          // log.log('calculateTxnDelta is ', calculateTxnDelta);

          /*
           * CALC METHOD 1 - 'R' Calculating the sum of original transactions associated with currentBalance
           */
          if (calculateTxnDelta || calculateTxnDelta === 0) {
            earliestTransactionDate = txns.size ? txns.first().postedOn : moment().format('YYYY-MM-DD');

            endingBalance = account.currentBalanceAsOf;
            endingBalanceNoRecurring = account.currentBalanceAsOf;
            currentBalance = 0;
            txnsDeltaFromCurrentBalance = endingBalance;
            finalBalance = endingBalance;

            txns.forEach((txn) => {
              if (!moment(txn.postedOn).isSameOrBefore(moment(account.currentBalanceAsOfOn))) {
                finalBalance += Number(txn.amount);
              }

              if (includeTransactionInBalance(
                txn,
                {
                  goalAccountIds,
                  includeGoalTxnsInBalances,
                  recurringSetting: acctReminderSetting,
                  transactionsForAccountId: txns,
                }
              )) {
                if (moment(txn.postedOn).isBefore(earliestTransactionDate)) {
                  earliestTransactionDate = txn.postedOn;
                }
                if (!isUnacceptedScheduledTxn(txn) && moment(txn.postedOn).isSameOrBefore(moment(account.currentBalanceAsOfOn))) {
                  txnsDeltaFromCurrentBalance -= Number(txn.amount);
                } else {
                  endingBalance += Number(txn.amount);
                  endingBalanceNoRecurring += (isUnacceptedScheduledTxn(txn) ? 0 : Number(txn.amount));
                }
                if (moment(txn.postedOn).isAfter(moment(), 'day') && (!isAcme || !isUnacceptedScheduledTxn(txn))) {
                  currentBalance += Number(txn.amount);
                }
              }

              if (txn.coa?.type === 'ACCOUNT' && goalAccountIds.contains(txn.coa?.id) && !goalAccountIds.contains(txn.accountId)) {
                goalsBalance += Number(txn.amount);
              }
            });
            currentBalance = endingBalance - currentBalance;
            // log.log('NEW TXNDELTA IS ', txnsDeltaFromCurrentBalance);
            // log.log('ending Balance is ', endingBalance);
            // log.log('ending Balance No Recurring is ', endingBalanceNoRecurring);

          } else {
            /*
             * CALC METHOD 2 and 3 - Calculating balance starting with either balanceAsOf or txnsDeltaFromCurrentBalance
             */
            // if we have a valid sum of original transactions in the cache, use it
            const useTxnDeltaAmount = balMap[cacheKey] && balMap[cacheKey].txnsDeltaFromCurrentBalance !== null;

            log.log('======================= CALC METHOD 2 FOR ', account.name, useTxnDeltaAmount, balMap[cacheKey] && balMap[cacheKey].txnsDeltaFromCurrentBalance);

            earliestTransactionDate = txns.size ? txns.first().postedOn : moment().format('YYYY-MM-DD');

            endingBalance = useTxnDeltaAmount ? balMap[cacheKey].txnsDeltaFromCurrentBalance : Number(account.balanceAsOf);
            txnsDeltaFromCurrentBalance = useTxnDeltaAmount ? balMap[cacheKey].txnsDeltaFromCurrentBalance : null;
            currentBalance = endingBalance;
            finalBalance = endingBalance;
            endingBalanceNoRecurring = endingBalance;

            txns.forEach((txn) => {
              if (useTxnDeltaAmount || (!account.balanceAsOfOn || moment(txn.postedOn).isAfter(moment(account.balanceAsOfOn)))) {
                finalBalance += Number(txn.amount);
              }

              if (includeTransactionInBalance(
                txn,
                {
                  goalAccountIds,
                  includeGoalTxnsInBalances,
                  recurringSetting: acctReminderSetting,
                  transactionsForAccountId: txns,
                }
              )) {
                if (moment(txn.postedOn).isBefore(earliestTransactionDate)) {
                  earliestTransactionDate = txn.postedOn;
                }
                // if we are using the 'R' value, date does not matter, but if we are calculating from balanceAsOfOn, we
                // only count transactions that are after the balanceAsOfOn date
                if (useTxnDeltaAmount || (!account.balanceAsOfOn || moment(txn.postedOn).isAfter(moment(account.balanceAsOfOn)))) {
                  endingBalance += Number(txn.amount);
                  endingBalanceNoRecurring += (isUnacceptedScheduledTxn(txn) ? 0 : Number(txn.amount));
                  if (moment(txn.postedOn).isSameOrBefore(moment(), 'day')) {
                    currentBalance += Number(txn.amount);
                  }
                }
              }

              if (txn.coa?.type === 'ACCOUNT' && goalAccountIds.contains(txn.coa?.id) && !goalAccountIds.contains(txn.accountId)) {
                goalsBalance += Number(txn.amount);
              }
            });
            // log.log('ending Balance is ', endingBalance, currentBalance);
            // log.log('ending Balance No Recurring is ', endingBalanceNoRecurring);
          }
        }

        endingBalance = balRound(endingBalance);
        endingBalanceNoRecurring = balRound(endingBalanceNoRecurring);
        currentBalance = balRound(currentBalance);
        finalBalance = balRound(finalBalance);
        goalsBalance = balRound(goalsBalance);

        if (account.type !== 'INVESTMENT') {
          const balancesForAccountId = new AccountBalances({
            accountId: account.id,
            accountType: account.type,
            onlineBalance: !isConnected ? currentBalance : account.onlineBalance,
            endingBalance,
            endingBalanceNoRecurring,
            currentBalance,
            finalBalance,
            goalsBalance,
            earliestTransactionDate,
            txnsDeltaFromCurrentBalance,  // 'R'
          });
          balancesByAccountId.set(account.id, balancesForAccountId);

          // Set Cache
          if (setCache) {
            balMap[cacheKey] = {
              endingBalance,
              endingBalanceNoRecurring,
              currentBalance,
              finalBalance,
              goalsBalance,
              txns,
              account,
              earliestTransactionDate,
              txnsDeltaFromCurrentBalance,
              acctReminderSetting,
              includeGoalTxnsInBalances,
            };
            // Set the optimal Cache
            setOptimalCache(transactionsStore, 'acctBalances', cacheString, strictCacheString, balancesForAccountId, 50);
          }
        }
      });
    })
);

export const getBalanceForAccountAtDate = createSelector(
  (state, props) => props.accountId,
  (state, props) => props.date,
  getTransactionsByAccountId,
  getAccountsById,
  getBalancesByAccountId,
  (accountId, date, txnsByAccountId, accountsById, balancesByAccountId) => {

    const balanceObject = balancesByAccountId.get(accountId);
    if (balanceObject) {
      let runningBalance = balanceObject.endingBalance;
      let txns = txnsByAccountId.get(accountId);
      if (txns) {

        // sort the transactions by date
        txns = txns.sort((a, b) =>
          new Date(b.postedOn).valueOf() - new Date(a.postedOn).valueOf());

        // now add from the top until we see a transaction on or before the given date
        txns.some((txn) => {
          if (moment(txn.postedOn).isSameOrBefore(moment(date))) {
            return true;
          }
          if (!isUnacceptedScheduledTxn(txn)) {
            runningBalance -= Number(txn.amount);
          }
          return false;
        });
      }
      // todo "TypeError: Cannot read property 'toFixed' of null"
      return runningBalance.toFixed(2);
    }
    return null;
  }
);


const investmentsBalMap = {};

const getInvestmentBalancesByAccountIdQuicken = createSelector(
  getAccountsById,
  getIHoldsByAccountId,
  (accounts, iHoldsByAccountId) => (
    (new ImmutableMap()).withMutations((balancesByAccountId) => {
      accounts.forEach((account) => {

        if (account.type !== 'INVESTMENT') return;

        const holdings = iHoldsByAccountId.get(account.id);

        // Try and use the cache. Holdings are just a one shot load unlike the other accounts so this doesn't get used.
        if (holdings && (investmentsBalMap[account.id] && investmentsBalMap[account.id].holdings === holdings)) {
          const endBal = investmentsBalMap[account.id] ? investmentsBalMap[account.id].endingBalance : 0;
          const currBal = investmentsBalMap[account.id] ? investmentsBalMap[account.id].currentBalance : 0;
          const eDate = investmentsBalMap[account.id] ? investmentsBalMap[account.id].earliestTransactionDate : null;
          balancesByAccountId.set(account.id, new AccountBalances({
            accountId: account.id,
            accountType: account.type,
            endingBalance: endBal,                // Total of everything
            onlineBalance: account.onlineBalance, // What the bank says it is
            currentBalance: currBal,              // Today's balance
            earliestTransactionDate: eDate,
          }));
          // log.log('======================= CACHE HIT OR EMPTY HOLDINGS FOR ', account.name);
          return;
        }
        // log.log('======================= RECALCULATING HOLDING BALANCES FOR ', account.name);

        /*
          Loop over the investment holdings for this account and determine:
            1. The balanceAsOfOn from the latest modifiedAt date from each.
            2. The earliestTransactionDate from the earliest modifiedAt date from each.
            3. The endingBalance by summing latestPrice * numberOfShares + the account's cash balance
            4. The currentBalance = endingBalance
         */

        let balanceAsOfOn;
        let earliestTransactionDate = moment();
        let endingBalance = 0.0;

        if (holdings) {
          holdings.forEach((holding) => {
            if (!balanceAsOfOn) balanceAsOfOn = moment(holding.modifiedAt).format('MM-DD-YYYY');
            if (!earliestTransactionDate) earliestTransactionDate = moment(holding.modifiedAt).format('MM-DD-YYYY');
            if (moment(holding.modifiedAt).format('MM-DD-YYYY') > new Date(balanceAsOfOn)) {
              balanceAsOfOn = moment(holding.modifiedAt).format('MM-DD-YYYY');
            }
            if (moment(holding.modifiedAt).format('MM-DD-YYYY') < new Date(earliestTransactionDate)) {
              earliestTransactionDate = moment(holding.modifiedAt).format('MM-DD-YYYY');
            }
            endingBalance += (holding.numberOfShares * holding.latestPrice);
          });
        }

        endingBalance += account.cashBalance;

        const balancesForAccountId = new AccountBalances({
          accountId: account.id,
          accountType: account.type,
          currentBalance: endingBalance,
          onlineBalance: endingBalance,
          endingBalance,
          earliestTransactionDate,
        });

        balancesByAccountId.set(account.id, balancesForAccountId);
        investmentsBalMap[account.id] = {
          endingBalance,
          currentBalance: endingBalance,
          holdings,
          earliestTransactionDate,
        };
      });
    })
  )
);

const getInvestmentBalancesByAccountIdSimplifi = createSelector(
  createTrimSelector(getInvestmentAccounts, (investmentAccount) => ({
    id: investmentAccount.id,
    type: investmentAccount.type,
    onlineBalance: investmentAccount.onlineBalance,
  })),
  getInvestmentHoldingsBalancesByAccountId,
  (investmentAccounts, holdingsBalancesByAccountId) => investmentAccounts.map((account) => AccountBalances({
    accountId: account.id,
    accountType: account.type,
    onlineBalance: account.onlineBalance || 0,
    currentBalance: account.onlineBalance ?? holdingsBalancesByAccountId.get(account.id),
  })),
);

export const getAllBalancesByAccountId = createSelector(
  getBalancesByAccountId,
  isAcme ? getInvestmentBalancesByAccountIdSimplifi : getInvestmentBalancesByAccountIdQuicken,
  (balancesByAccountId, investmentBalancesByAccountId) => balancesByAccountId.merge(investmentBalancesByAccountId)
);
